import React, { useCallback, useEffect, useState } from 'react';

import { Botao, Chip, Modal, Nota, Skeleton, VarianteChip } from '../../../ui';
import { formatarDataHora } from '../../../utils/data';
import { getFipeStatus, StatusEspelhoFipe } from '../../../api/veiculos/veiculoApi';

/**
 * Item 14 — leitura do estado da base FIPE que o SAAC usa.
 * Reescrito sobre o design system na at-67 (frente D/D5).
 *
 * ⚠️ **Read-only de propósito, sem botão de sincronizar.** O SAAC não sincroniza a
 * FIPE desde a at-16: ele lê o banco do serviço `fipe-atual`, que tem painel próprio
 * no SOAC. Um botão aqui prometeria algo que este sistema não faz.
 *
 * ⚠️ **Não há `ErrorState` aqui, e isso é correto:** `getFipeStatus` **nunca
 * lança** (at-47) — falha de rede volta como `situacao: 'DESCONHECIDA'` e o banco
 * do fipe-atual fora do ar volta como `INDISPONIVEL`, vindo do backend. Os dois já
 * **são** estados com mensagem própria; inventar um erro de tela por cima
 * duplicaria a informação e esconderia a distinção entre eles.
 *
 * 📌 O que a at-67 mudou: chips com `rgba()` escrito à mão viraram `Chip` do kit
 * (com as variantes semânticas), o "Consultando…" virou `Skeleton`, os 12 blocos
 * `style={{}}` sumiram, e `formatarData` local deu lugar a `formatarDataHora`
 * (`utils/data.ts`, at-66), que trata `null` antes do parse.
 */

const VARIANTE: Record<StatusEspelhoFipe['situacao'], { texto: string; chip: VarianteChip }> = {
  EM_DIA: { texto: 'Em dia', chip: 'ok' },
  /* ⚠️ `PARCIAL` é âmbar, não vermelho: a base está utilizável, só misturada
     entre dois meses de referência enquanto uma varredura corre (at-47). */
  PARCIAL: { texto: 'Parcialmente atualizada', chip: 'pendente' },
  DEFASADA: { texto: 'Desatualizada', chip: 'perigo' },
  INDISPONIVEL: { texto: 'Inacessível', chip: 'perigo' },
  DESCONHECIDA: { texto: 'Não avaliada', chip: 'neutro' },
};

interface ModalBaseFipeProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalBaseFipe: React.FC<ModalBaseFipeProps> = ({ isOpen, onClose }) => {
  const [status, setStatus] = useState<StatusEspelhoFipe | null>(null);
  const [carregando, setCarregando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    // getFipeStatus nunca lança — devolve DESCONHECIDA em falha de rede.
    const res = await getFipeStatus();
    setStatus(res);
    setCarregando(false);
  }, []);

  useEffect(() => {
    if (isOpen) void carregar();
  }, [isOpen, carregar]);

  const rotulo = status ? VARIANTE[status.situacao] ?? VARIANTE.DESCONHECIDA : null;

  return (
    <Modal
      aberto={isOpen}
      titulo="Base FIPE"
      descricao="O mês de referência da tabela que o SAAC consulta."
      aoFechar={onClose}
      rodape={
        <Botao variante="secundario" onClick={onClose}>
          Fechar
        </Botao>
      }
    >
      {carregando && !status && <Skeleton linhas={3} altura="tw-h-12" />}

      {status && rotulo && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <Chip variante={rotulo.chip}>{rotulo.texto}</Chip>
            {typeof status.totalVeiculos === 'number' && (
              <span className="tw-text-xs tw-text-texto-suave">
                {status.totalVeiculos.toLocaleString('pt-BR')} veículo(s)
              </span>
            )}
          </div>

          <p className="tw-m-0 tw-text-sm tw-text-texto-corpo">{status.mensagem}</p>

          {!!status.referencias?.length && (
            <div className="tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-white tw-p-3">
              <p className="tw-mb-2 tw-mt-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-texto-suave">
                Meses de referência na base
              </p>
              <dl className="tw-m-0 tw-flex tw-flex-col tw-gap-1">
                {status.referencias.map((referencia) => (
                  <div
                    key={referencia.competencia}
                    className="tw-flex tw-items-baseline tw-justify-between tw-gap-3"
                  >
                    <dt className="tw-text-xs tw-text-texto-corpo">{referencia.rotulo}</dt>
                    <dd className="tw-m-0 tw-text-xs tw-text-texto-suave">
                      {referencia.quantidade.toLocaleString('pt-BR')} ({referencia.percentual}%)
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <dl className="tw-m-0 tw-flex tw-flex-col tw-gap-1 tw-text-xs">
            <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2">
              <dt className="tw-font-semibold tw-text-texto-corpo">Última gravação no espelho:</dt>
              <dd className="tw-m-0 tw-text-texto-suave">
                {status.ultimaAtualizacao ? formatarDataHora(status.ultimaAtualizacao) : 'não disponível'}
              </dd>
            </div>
            <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2">
              <dt className="tw-font-semibold tw-text-texto-corpo">Última sincronização completa:</dt>
              <dd className="tw-m-0 tw-text-texto-suave">
                {status.ultimaSincronizacao
                  ? formatarDataHora(status.ultimaSincronizacao)
                  : 'não disponível'}
              </dd>
            </div>
          </dl>

          <Nota>
            O SAAC <b>não sincroniza</b> a tabela FIPE — ele lê a base do serviço{' '}
            <b>fipe-atual</b>, que roda sozinho (verificação diária às 8h; sincroniza quando a
            última completa tem 15 dias ou mais). Para acompanhar ou disparar uma sincronização,
            use <b>SOAC → Sincronização FIPE</b>.
          </Nota>
        </div>
      )}
    </Modal>
  );
};

export default ModalBaseFipe;
